import { Link } from 'react-router-dom';
import { formatDate } from '../../lib/format';
import type { PlannedPostRead } from '../../types/api';
import { errorLabel } from './automation';

interface NeedsReviewStripProps {
  /** Plans parked at `needs_review`, any month. */
  posts: readonly PlannedPostRead[];
  onOpen: (post: PlannedPostRead) => void;
  onReset: (post: PlannedPostRead) => void;
}

/**
 * The plans that stopped and are waiting for a person, above the grid.
 *
 * This is the calendar's one notification surface: there is no email and no
 * toast, so a plan that generated its draft, or could not go on, has to be
 * visible the moment the screen opens — whatever month is showing. Empty, it
 * renders nothing at all.
 */
export function NeedsReviewStrip({ posts, onOpen, onReset }: NeedsReviewStripProps) {
  if (posts.length === 0) return null;

  return (
    <section className="review-strip" aria-labelledby="review-strip-title">
      <h2 className="review-strip__title" id="review-strip-title">
        Needs your review
      </h2>
      <ul className="review-strip__list">
        {posts.map((post) => {
          const reason = errorLabel(post.automation_error);
          return (
            <li className="review-strip__item" key={post.id}>
              <button className="review-strip__post" type="button" onClick={() => onOpen(post)}>
                <span className="review-strip__post-title">{post.title}</span>
                <span className="review-strip__post-meta">
                  {formatDate(post.scheduled_on)}
                  {reason ? ` · ${reason}` : ' · The draft is ready.'}
                </span>
              </button>
              <div className="review-strip__actions">
                {post.generation_request_id ? (
                  <Link
                    className="button button--primary button--small"
                    to={`/review/${post.generation_request_id}`}
                  >
                    Review
                  </Link>
                ) : null}
                <button
                  className="button button--ghost button--small"
                  type="button"
                  onClick={() => onReset(post)}
                >
                  Reset
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
